import { useEffect, useState } from "react";
import UserLayout from "@/components/UserLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Search, ShieldCheck, Zap, RefreshCw, Copy, TrendingUp } from "lucide-react";
import { ServiceIcon } from "@/components/ServiceIcon";

const TIPS = [
  { q: "Kod ne kadar sürede gelir?", a: "Çoğu servis için SMS kodu 1-3 dakika içinde ulaşır. Yoğunluk durumunda 5-10 dakika kadar uzayabilir." },
  { q: "Numarayı ne zaman iptal edebilirim?", a: "Satın aldıktan sonra 4. dakikadan itibaren manuel iptal edip iade alabilirsiniz. 20. dakikada kod gelmezse otomatik iade yapılır." },
  { q: "SMS kodu geldikten sonra iptal edebilir miyim?", a: "Hayır. Kod aldığınız anda numaranın kullanım hakkı size geçer, iade yapılamaz." },
  { q: "Aynı numarayı tekrar kullanabilir miyim?", a: "Sanal numaralarımız tek kullanımlıktır. Her aktivasyon için yeni bir numara alınız." },
  { q: "Türkiye numarası mı yoksa yurt dışı mı almalıyım?", a: "WhatsApp/Instagram gibi platformlar bazı ülkeleri kısıtlar. Reddedilme olursa farklı ülke deneyin." },
];

export default function SmsAI() {
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [prices, setPrices] = useState({}); // key: service->[{country, price}]
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/countries")])
      .then(([s, c]) => { setServices(s.data); setCountries(c.data); })
      .catch(() => {});
  }, []);

  const analyze = async (svcCode) => {
    if (prices[svcCode]) return; // already loaded
    setPrices((p) => ({ ...p, [svcCode]: "loading" }));
    const sampleCountries = countries.slice(0, 5);
    const results = [];
    for (const c of sampleCountries) {
      try {
        const { data } = await api.get(`/price?country_id=${c.id}&service=${svcCode}`);
        results.push({ country: c.country_name, price: data.final_price, stock: data.count });
      } catch (e) { console.warn("Price sample failed:", e); }
    }
    if (results.length === 0) {
      toast.error("Fiyat bilgisi alınamadı");
      setPrices((p) => { const cp = { ...p }; delete cp[svcCode]; return cp; });
      return;
    }
    results.sort((a, b) => a.price - b.price);
    setPrices((p) => ({ ...p, [svcCode]: results }));
  };

  const filtered = services.filter((s) => !q.trim() || s.name.toLowerCase().includes(q.toLowerCase()) || s.code.includes(q.toLowerCase()));

  return (
    <UserLayout>
      <div className="mb-6">
        <div className="inline-flex items-center gap-1.5 border border-[hsl(217,91%,60%)]/40 bg-[hsl(217,91%,60%)]/10 text-[hsl(217,91%,68%)] px-3 py-1 rounded-full text-xs font-bold mb-3">
          <Sparkles className="w-3 h-3" /> Akıllı Fiyat Karşılaştırıcı
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black">SMS AI</h1>
        <p className="text-white/60 text-sm md:text-base mt-2 max-w-2xl">
          En uygun ülke ve fiyatı hızlıca keşfet, karar vermeden önce servisin gerçek zamanlı stoklarını incele.
        </p>
      </div>

      {/* Tips */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        <StatCard icon={<Zap className="w-4 h-4" />} label="Ortalama teslim" value="1-3 dk" accent="hsl(217,91%,60%)" />
        <StatCard icon={<RefreshCw className="w-4 h-4" />} label="Otomatik iade" value="20. dk" accent="hsl(142,71%,55%)" />
        <StatCard icon={<ShieldCheck className="w-4 h-4" />} label="Aktif ülke sayısı" value={`${countries.length}+`} accent="hsl(0,84%,60%)" />
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <input
          data-testid="smsai-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Servis ara (WhatsApp, Telegram, ...)"
          className="w-full bg-[hsl(0,0%,6%)] border border-white/10 focus:border-[hsl(217,91%,60%)] outline-none pl-11 pr-4 py-3.5 text-sm rounded-2xl"
        />
      </div>

      <div className="space-y-3 mb-10" data-testid="smsai-list">
        {filtered.map((s) => (
          <div key={s.code} className="border border-white/10 bg-[hsl(0,0%,7%)] rounded-2xl p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <ServiceIcon code={s.code} size="md" />
                <div>
                  <div className="font-display font-bold text-lg">{s.name}</div>
                  <div className="text-xs text-white/50 uppercase tracking-wider font-mono">{s.code}</div>
                </div>
              </div>
              <button
                onClick={() => analyze(s.code)}
                data-testid={`analyze-${s.code}`}
                disabled={prices[s.code] === "loading"}
                className="btn-brand text-xs font-bold px-4 py-2 rounded-xl inline-flex items-center gap-2"
              >
                {prices[s.code] === "loading" ? "Sorgulanıyor…" : (<><TrendingUp className="w-3.5 h-3.5" /> Fiyatları Analiz Et</>)}
              </button>
            </div>

            {Array.isArray(prices[s.code]) && (
              <div className="mt-4 border-t border-white/10 pt-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">En uygun 5 ülke</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {prices[s.code].map((p) => (
                    <div key={`${p.country}-${p.price}`} className="border border-white/10 rounded-xl p-3 bg-white/[0.02]">
                      <div className="text-xs text-white/60 truncate">{p.country}</div>
                      <div className="font-mono font-black text-[hsl(142,71%,55%)] text-lg">₺{Number(p.price).toFixed(2)}</div>
                      <div className="text-[10px] text-white/40">Stok: {p.stock}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* FAQ */}
      <div>
        <h2 className="font-display font-black text-2xl mb-4">Sıkça Sorulan Sorular</h2>
        <div className="space-y-2">
          {TIPS.map((t, i) => (
            <details key={t.q} className="border border-white/10 bg-[hsl(0,0%,7%)] rounded-2xl p-4 group" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer font-bold text-white/90 flex items-center justify-between">
                {t.q}
                <span className="text-white/40 group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <p className="text-white/60 text-sm mt-3 leading-relaxed">{t.a}</p>
            </details>
          ))}
        </div>
      </div>
    </UserLayout>
  );
}

function StatCard({ icon, label, value, accent }) {
  return (
    <div className="border border-white/10 bg-[hsl(0,0%,7%)] rounded-2xl p-4 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</div>
        <div className="font-display font-black text-xl text-white">{value}</div>
      </div>
    </div>
  );
}
